import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Download, Lock, Unlock, Calculator } from "lucide-react";

function periodoMes() {
  return todayISO().slice(0, 7);
}

export default function Declaraciones() {
  const [tab, setTab] = useState("f29");
  const [periodo, setPeriodo] = useState(periodoMes());
  const [anio, setAnio] = useState(Number(todayISO().slice(0, 4)));
  const [f29, setF29] = useState(null);
  const [dj1879, setDj1879] = useState(null);
  const [dj1887, setDj1887] = useState(null);
  const [f22, setF22] = useState(null);
  const [periodos, setPeriodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ajuste, setAjuste] = useState({ gastos_rechazados: "", ingresos_no_renta: "" });

  const loadF29 = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reportes/f29-borrador", { params: { periodo } });
      setF29(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadDj = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/reportes/dj-1879", { params: { anio } }),
        api.get("/reportes/dj-1887", { params: { anio } }),
      ]);
      setDj1879(a.data);
      setDj1887(b.data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadF22 = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/reportes/f22-borrador", {
        anio,
        gastos_rechazados: parseFloat(ajuste.gastos_rechazados) || 0,
        ingresos_no_renta: parseFloat(ajuste.ingresos_no_renta) || 0,
      });
      setF22(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodos = async () => {
    try {
      const { data } = await api.get("/periodos-contables", { params: { anio } });
      setPeriodos(data.periodos || []);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  useEffect(() => {
    if (tab === "f29") loadF29();
    if (tab === "dj") loadDj();
    if (tab === "f22") loadF22();
    if (tab === "periodos") loadPeriodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const descargarArchivo = async (path, nombre, formato = "csv") => {
    try {
      const { data } = await api.get(path, { params: { anio, formato }, responseType: "blob" });
      const mime = formato === "pdf" ? "application/pdf" : "text/csv";
      const url = URL.createObjectURL(new Blob([data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const togglePeriodo = async (p, cerrado) => {
    try {
      await api.post("/periodos-contables/cerrar", { periodo: p, cerrado });
      toast.success(cerrado ? `Período ${p} cerrado` : `Período ${p} reabierto`);
      loadPeriodos();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const cierreEjercicio = async () => {
    if (!window.confirm(`¿Generar asiento de cierre del ejercicio ${anio}?`)) return;
    try {
      const { data } = await api.post("/periodos-contables/cierre-ejercicio", { anio, confirmar: true });
      toast.success(`Cierre ${anio}: utilidad ${fmtCLP(data.utilidad_neta || 0)}`);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const aperturaEjercicio = async () => {
    if (!window.confirm(`¿Registrar apertura del ejercicio ${anio}? (requiere cierre de ${anio - 1})`)) return;
    try {
      const { data } = await api.post("/periodos-contables/apertura-ejercicio", { anio, confirmar: true });
      toast.success(data.aviso || `Apertura ${anio}: ${data.cuentas} cuentas`);
      loadPeriodos();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const tabs = [
    { id: "f29", label: "F29" },
    { id: "dj", label: "DJ 1879 / 1887" },
    { id: "f22", label: "F22 / RLI" },
    { id: "periodos", label: "Períodos" },
  ];

  return (
    <Page>
      <PageHeader
        title="Declaraciones y períodos"
        subtitle="Borradores F29, DJ, F22/RLI y cierre/bloqueo de meses contables"
        testid="decl-header"
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`text-sm px-3 py-1.5 rounded-sm border ${tab === t.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "f29" && (
        <section className="bg-card border border-border rounded-md p-5 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm">Período
              <input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="block mt-1 border border-border rounded-sm px-2 py-1.5 text-sm" />
            </label>
            <button onClick={loadF29} className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
              <Calculator className="h-4 w-4" /> Calcular
            </button>
          </div>
          {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}
          {f29 && (
            <>
              <p className="text-[11px] text-muted-foreground">{f29.aviso}</p>
              <p className="text-sm">Vencimiento: <strong>{f29.vencimiento || "—"}</strong></p>
              <table className="acc-table">
                <thead><tr><th>Código</th><th className="num">Monto</th></tr></thead>
                <tbody>
                  {Object.entries(f29.codigos || {}).map(([k, v]) => (
                    <tr key={k}><td>{k}</td><td className="num">{typeof v === "number" && k !== "115" ? fmtCLP(v) : v}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </section>
      )}

      {tab === "dj" && (
        <section className="bg-card border border-border rounded-md p-5 space-y-4">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm">Año
              <input type="number" value={anio} onChange={(e) => setAnio(+e.target.value)} className="block mt-1 border border-border rounded-sm px-2 py-1.5 text-sm w-28" />
            </label>
            <button onClick={loadDj} className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm">Actualizar</button>
            <button onClick={() => descargarArchivo("/reportes/dj-1879", `dj1879_${anio}.csv`)} className="border border-border text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-1">
              <Download className="h-3.5 w-3.5" /> CSV 1879
            </button>
            <button onClick={() => descargarArchivo("/reportes/dj-1879", `certificado_dj1879_${anio}.pdf`, "pdf")} className="border border-border text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-1">
              <Download className="h-3.5 w-3.5" /> PDF 1879
            </button>
            <button onClick={() => descargarArchivo("/reportes/dj-1887", `dj1887_${anio}.csv`)} className="border border-border text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-1">
              <Download className="h-3.5 w-3.5" /> CSV 1887
            </button>
            <button onClick={() => descargarArchivo("/reportes/dj-1887", `certificado_dj1887_${anio}.pdf`, "pdf")} className="border border-border text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-1">
              <Download className="h-3.5 w-3.5" /> PDF 1887
            </button>
          </div>
          {dj1879 && (
            <div>
              <h3 className="font-display text-lg mb-1">DJ 1879 — Honorarios</h3>
              <p className="text-sm text-muted-foreground mb-2">{dj1879.prestadores ?? dj1879.detalle?.length ?? 0} prestadores · {dj1879.aviso}</p>
              <p className="text-sm">Total retenido: <strong>{fmtCLP(dj1879.total_retenido || dj1879.total_retencion || 0)}</strong></p>
            </div>
          )}
          {dj1887 && (
            <div>
              <h3 className="font-display text-lg mb-1">DJ 1887 — Sueldos</h3>
              <p className="text-sm text-muted-foreground mb-2">{dj1887.trabajadores} trabajadores · {dj1887.aviso}</p>
              <p className="text-sm">Renta imponible: <strong>{fmtCLP(dj1887.total_renta_imponible)}</strong> · IU: <strong>{fmtCLP(dj1887.total_impuesto_unico)}</strong></p>
            </div>
          )}
        </section>
      )}

      {tab === "f22" && (
        <section className="bg-card border border-border rounded-md p-5 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm">Año
              <input type="number" value={anio} onChange={(e) => setAnio(+e.target.value)} className="block mt-1 border border-border rounded-sm px-2 py-1.5 text-sm w-28" />
            </label>
            <label className="text-sm">Gastos rechazados
              <input type="number" min={0} value={ajuste.gastos_rechazados} onChange={(e) => setAjuste({ ...ajuste, gastos_rechazados: e.target.value })} className="block mt-1 border border-border rounded-sm px-2 py-1.5 text-sm w-36" />
            </label>
            <label className="text-sm">Ingresos no renta
              <input type="number" min={0} value={ajuste.ingresos_no_renta} onChange={(e) => setAjuste({ ...ajuste, ingresos_no_renta: e.target.value })} className="block mt-1 border border-border rounded-sm px-2 py-1.5 text-sm w-36" />
            </label>
            <button onClick={loadF22} className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
              <Calculator className="h-4 w-4" /> Calcular RLI
            </button>
          </div>
          {f22 && (
            <>
              <p className="text-[11px] text-muted-foreground">{f22.aviso}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <Fila label="Utilidad contable" value={fmtCLP(f22.utilidad_contable)} />
                <Fila label="RLI" value={fmtCLP(f22.rli?.rli)} fuerte />
                <Fila label={`IDPC (${f22.idpc?.tasa_idpc_pct} %)`} value={fmtCLP(f22.idpc?.impuesto_primera_categoria)} />
                <Fila label="PPM imputados" value={fmtCLP(f22.idpc?.ppm_imputados)} />
                <Fila label="A pagar" value={fmtCLP(f22.idpc?.a_pagar)} tono="text-primary" />
                <Fila label="Por recuperar" value={fmtCLP(f22.idpc?.por_recuperar)} />
              </div>
            </>
          )}
        </section>
      )}

      {tab === "periodos" && (
        <section className="bg-card border border-border rounded-md p-5 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="text-sm">Año
              <input type="number" value={anio} onChange={(e) => setAnio(+e.target.value)} className="block mt-1 border border-border rounded-sm px-2 py-1.5 text-sm w-28" />
            </label>
            <button onClick={loadPeriodos} className="border border-border text-sm px-3 py-1.5 rounded-sm">Actualizar</button>
            <button onClick={cierreEjercicio} className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm">Cierre de ejercicio</button>
            <button onClick={aperturaEjercicio} className="border border-border text-sm px-3 py-1.5 rounded-sm">Apertura de ejercicio</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {Array.from({ length: 12 }, (_, i) => {
              const p = `${anio}-${String(i + 1).padStart(2, "0")}`;
              const row = periodos.find((x) => x.periodo === p);
              const cerrado = !!row?.cerrado;
              return (
                <button
                  key={p}
                  onClick={() => togglePeriodo(p, !cerrado)}
                  className={`text-left border rounded-sm px-3 py-2 text-sm ${cerrado ? "border-destructive/40 bg-destructive/5" : "border-border hover:bg-muted"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">{p}</span>
                    {cerrado ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5 text-muted-foreground" />}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{cerrado ? "Cerrado" : "Abierto"}</div>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </Page>
  );
}

function Fila({ label, value, fuerte, tono }) {
  return (
    <div className={`flex justify-between gap-3 py-1.5 border-b border-border/60 ${fuerte ? "font-semibold" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={`num ${tono || ""}`}>{value}</span>
    </div>
  );
}
